import CustomDivider from '../../reusable/CustomDivider';
import LollipopChart from '../../reusable/LollipopChart';
import { useTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';
import { trimTo2Decimals, formatNumber } from '../../../utils';

interface DemographicsContentSectionProps {
  header: { title: string; description: string };
  studentsCount: number;
  countDescription: string;
  genderData: { [key: string]: number };
  dotColors: string[];
  raceChartData: { labels: (string | string[])[]; data: number[]; color: string };
  otherData: {
    lowIncome: number;
    disabilities: number;
    englishLearner: { learner: number; native: number; other?: number };
  };
  classes?: string[];
}

function DemographicsContentSection({
  header,
  studentsCount,
  countDescription,
  genderData,
  dotColors,
  raceChartData,
  otherData,
  classes,
}: DemographicsContentSectionProps) {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  const classNames = classes ? classes.join(' ') : '';

  const sortedGenderData = Object.entries(genderData).sort((dataPoint1, dataPoint2) => dataPoint2[1] - dataPoint1[1]);

  const calculateChartHeight = (rows: number) => {
    // base height of 80px + 45px for each row
    if (rows === 1) return '80px';

    let height = rows * 45 + 80;

    return `${height}px`;
  };

  return (
    <div className="demographics-content-section">
      <div className="demographics-content-section-header">
        <span>{header.title}</span>
        <span className="header-description">{header.description}</span>
      </div>
      <p className="students-count-info no-margin" data-testid="students-count-info">
        <span className="students-count">{formatNumber(studentsCount)}</span>
        <span className="students-count-description">{countDescription}</span>
      </p>
      <CustomDivider orientation="horizontal" className="light" />
      <div className="demographics-content-statistics">
        <span className="header-text">Gender</span>
        {sortedGenderData.length > 0 ? (
          <div className="bars">
            {sortedGenderData.map(([key, value], index) => (
              <div
                data-testid={`section-${key}`}
                key={key}
                style={{ width: `${value}%`, backgroundColor: dotColors[index] }}
                className={classNames}
              ></div>
            ))}
          </div>
        ) : (
          <p>Graphic not available!</p>
        )}
        {sortedGenderData.length > 0 && (
          <div className={`bar-percentages ${isSmallScreen ? 'small-screen' : ''}`}>
            {sortedGenderData.map(([key, value], index) => (
              <div key={key} className="legend" data-testid={`legend-${key}`}>
                <div style={{ backgroundColor: dotColors[index] }} className="color-dot"></div>
                <span>{trimTo2Decimals(value)}%</span>
                <p className="gender capitalized"> {key.toLowerCase()}</p>
              </div>
            ))}
          </div>
        )}
      </div>
      <CustomDivider orientation="horizontal" className="light" />
      <div className="chart-section">
        <span className="chart-title">Race/ethnicity</span>
        {raceChartData.data.length > 0 && raceChartData.labels.length > 0 ? (
          <div
            className="lollipop-chart-container"
            style={{ height: calculateChartHeight(raceChartData.labels.length) }}
          >
            <LollipopChart
              dataValues={raceChartData.data}
              labels={raceChartData.labels}
              secondDataset
              firstDatasetColor={raceChartData.color}
              secondDatasetColor="#E5E5E5"
              maintainAspectRatio={false}
            />
          </div>
        ) : (
          <p>Chart not available!</p>
        )}
      </div>

      {/* OTHER DATA */}
      <div className="demographics-content-statistics other-data">
        {/* SOCIO-DIS SECTION */}
        <div className={`socioeconomic-disability-status ${isSmallScreen ? 'small-screen' : ''}`}>
          {/* SOCIOECONOMIC */}
          <div className="socioeconomic-status" data-testid="socioeconomic-status">
            <div className="header">
              <span className="header-text">Socioeconomic status</span>
              <div className="line"></div>
            </div>
            <div className={`cell ${classNames}`}>
              <p className="cell-value">
                {otherData.lowIncome == null ? 'N/A' : `${trimTo2Decimals(otherData.lowIncome)}%`}
              </p>
              <span>Low income</span>
            </div>
          </div>
          {/* DISABILITIES */}
          <div className="disability-status" data-testid="disability-status">
            <div className="header">
              <span className="header-text">Disability status</span>
              <div className="line"></div>
            </div>
            <div className={`cell ${classNames}`}>
              <p className="cell-value">
                {otherData.disabilities == null ? 'N/A' : `${trimTo2Decimals(otherData.disabilities)}%`}
              </p>
              <span>Students with disabilities</span>
            </div>
          </div>
        </div>
        {/* ELL */}
        <div className="ell-status" data-testid="ell-status">
          <div className="header">
            <span className="header-text">ELL status</span>
            <div className="line"></div>
          </div>
          <div className={`el-native-status ${isSmallScreen ? 'small-screen' : ''}`}>
            <div className={`cell ${classNames}`}>
              <p className="cell-value">
                {otherData.englishLearner.learner == null
                  ? 'N/A'
                  : `${trimTo2Decimals(otherData.englishLearner.learner)}%`}
              </p>
              <span>English learners</span>
            </div>
            <div className={`cell ${classNames}`}>
              <p className="cell-value">
                {otherData.englishLearner.native == null
                  ? 'N/A'
                  : `${trimTo2Decimals(otherData.englishLearner.native)}%`}
              </p>
              <span>Native/Fluent</span>
            </div>
            {otherData.englishLearner.other ? (
              <div className={`cell ${classNames}`}>
                <p className="cell-value">
                  {otherData.englishLearner.other == null
                    ? 'N/A'
                    : `${trimTo2Decimals(otherData.englishLearner.other)}%`}
                </p>
                <span>Other</span>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

export default DemographicsContentSection;
